import {
  Box,
  Button,
  Divider,
  HStack,
  Link,
  Step,
  StepIcon,
  StepIndicator,
  StepNumber,
  StepSeparator,
  StepStatus,
  StepTitle,
  Stepper,
  Text,
  VStack,
  useMediaQuery,
  useSteps,
  useToast,
} from '@chakra-ui/react';
import InputForm from 'components/InputForm';
import InputMaskForm from 'components/InputMask';
import SelectForm from 'components/SelectForm';
import { DateTime } from 'luxon';
import React, { useCallback } from 'react';
import { useGoogleReCaptcha } from '@google-recaptcha/react';
import { useForm } from 'react-hook-form';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import { MdLock } from 'react-icons/md';
import { payWithCreditCard } from 'services/api.service';
import { validCpf } from 'services/validDocument';
import { calcularParcelasMaximas, maskCurrency } from 'utils/currency';
import { onlyNumbers } from 'utils/number';
import { InstallmentsConfig } from './types/ChargeTypes';
import { testarCC } from 'components/testarCC';
import { lookupCep, CepLookupResult } from 'utils/cepLookup';

const steps = [{ title: 'Identificação' }, { title: 'Endereço' }, { title: 'Pagamento' }];


export interface FormCardCreditCard {
  cardNumber: string;
  cardExpiration: string;
  cardCvv: string;
  cardHolderName: string;
  cardHolderCpf: string;
  installments: number;
  name: string;
  document: string;
  email: string;
  phone: string;
  zipCode: string;
  street: string;
  number: string;
  complement: string;
  district: string;
  city: string;
  state: string;
  pagSeguro3dsId?: string;
  method: 'CREDIT_CARD';
}

const EquisCard: React.FC<{
  id: string;
  antiFraudId: string;
  method: 'CREDIT_CARD';
  amount: number;
  feeType?: 'FROM_PAYER' | 'FROM_CUSTOMER';
  refetch: () => void;
  installmentsConfig: InstallmentsConfig;
}> = ({ id, feeType, amount, installmentsConfig, method, refetch, antiFraudId }) => {
  console.log(antiFraudId);
  const [isLargerThan800] = useMediaQuery('(min-width: 800px)');
  const toast = useToast();
  const { activeStep, goToNext, goToPrevious } = useSteps({
    index: 1,
    count: steps.length,
  });

  const [cep, setCep] = React.useState<CepLookupResult>();

  const [isLoading, setIsLoading] = React.useState(false);
  const { control, handleSubmit, setValue, setError } = useForm<FormCardCreditCard>({
    defaultValues: {
      installments: 1,
    },
    progressive: true,
    shouldFocusError: true,
  });

  const { executeV3 } = useGoogleReCaptcha();

  // Create an event handler so you can call the verification on button click event or form submit
  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeV3) {
      window.alert('Recaptcha has not been loaded');
      return;
    }

    return await executeV3('PAYMENT_CREDIT_CARD');
    // Do whatever you want with the token
  }, [executeV3]);

  const calculateInstallment = (amount: number, installment: number) => {
    if ((feeType ?? installmentsConfig.installmentsType) === 'FROM_PAYER') {
      const fee = (installmentsConfig.installments[installment]?.fee ?? 1) / 100;
      return amount / (1 - fee);
    }

    return amount;
  };

  const onSubmit = async (formData: FormCardCreditCard) => {
    if (activeStep !== 3) {
      goToNext();
      setIsLoading(false);
      return;
    }
    setIsLoading(true);

    const token = await handleReCaptchaVerify();
    const [expMonth, expYear] = formData.cardExpiration.split('/');
    payWithCreditCard(id, {
      ...formData,
      cardNumber: onlyNumbers(formData.cardNumber),
      cardHolderCpf: onlyNumbers(formData.cardHolderCpf),
      cardExpiration: `${expMonth}/20${expYear}`,
      recaptchaToken: token,
      installments: +formData.installments,
      state: cep?.uf,
      antiFraudId,
      zipCode: onlyNumbers(formData.zipCode),
      phone: onlyNumbers(formData.phone),
      document: onlyNumbers(formData.document),
      method,
    })
      .then(() => {
        refetch();
        setIsLoading(false);
        toast({
          title: 'Pagamento realizado com sucesso',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      })
      .catch(onError);

    return;
  };

  const onError = async (error) => {
    setIsLoading(false);
    toast({
      title: error?.response?.data?.message ?? 'Erro ao realizar pagamento',
      status: 'error',
      duration: 2000,
      isClosable: true,
    });
  };

  let maxQuantiyParcelas = calcularParcelasMaximas(+amount, installmentsConfig?.maxInstallments ?? 12);

  const onBlurCep = async (e: React.FocusEvent<HTMLInputElement>) => {
    const zipCode = e.target.value;
    if (zipCode.length === 9) {
      try {
        const cepData = await lookupCep(zipCode);

        setValue('street', cepData.logradouro);
        setValue('district', cepData.bairro);
        setValue('city', cepData.localidade);
        setValue('state', cepData.estado);

        setCep(cepData);
      } catch (error) {
        toast({
          title: 'CEP inválido',
          status: 'error',
          duration: 2000,
          isClosable: true,
        });
        setError('zipCode', {
          type: 'manual',
          message: 'CEP inválido',
        });
      }
    }
  };

  return (
    <VStack>
      <Stepper
        size="lg"
        w="100%"
        colorScheme="brand"
        index={activeStep}
        mb={5}
        orientation={isLargerThan800 ? 'horizontal' : 'vertical'}
      >
        {steps.map((step, index) => (
          <Step key={index}>
            <StepIndicator>
              <StepStatus
                complete={<StepIcon />}
                incomplete={<StepNumber />}
                active={<StepNumber />}
              />
            </StepIndicator>

            <Box flexShrink="0">
              <StepTitle>{step.title}</StepTitle>
            </Box>

            <StepSeparator />
          </Step>
        ))}
      </Stepper>
      <Text
        fontSize="lg"
        fontWeight="bold"
        textAlign="center"
      >
        {method === 'CREDIT_CARD' ? 'Cartão de crédito' : 'Cartão de débito'}
      </Text>

      <Divider mb={4} />

      <form
        onSubmit={handleSubmit(onSubmit)}
        style={{ maxWidth: 500, width: '100%' }}
      >
        {activeStep === 1 && (
          <>
            <VStack spacing={3}>
              <InputForm
                type="text"
                name="name"
                placeholder="Insira seu nome"
                control={control}
                label="Nome"
                rules={{
                  required: 'Campo obrigatório',
                  validate: {
                    valid: (value) => {
                      return value.split(' ').length > 1 || 'Nome inválido';
                    },
                  },
                }}
              />
              <InputMaskForm
                type="text"
                name="document"
                placeholder="999.999.999-99"
                control={control}
                mask="999.999.999-99"
                label="Documento"
                rules={{
                  required: 'Campo obrigatório',
                  minLength: {
                    value: 11,
                    message: 'CPF inválido',
                  },
                  validate: {
                    valid: (value) => {
                      value = onlyNumbers(value);

                      return validCpf(value) || 'Documento inválido';
                    },
                  },
                }}
              />

              <InputForm
                type="email"
                name="email"
                placeholder="email@email.com"
                control={control}
                label="E-mail"
                rules={{
                  required: 'Campo obrigatório',
                }}
              />

              <InputMaskForm
                type="tel"
                mask="(99) 99999-9999"
                name="phone"
                placeholder="(99) 99999-9999"
                control={control}
                label="Telefone"
                rules={{
                  required: 'Campo obrigatório',
                }}
              />

              <Button
                width="100%"
                mt={3}
                type="submit"
                colorScheme="brand"
                rightIcon={<FaArrowRight />}
              >
                Próximo
              </Button>
            </VStack>
          </>
        )}
        {activeStep === 2 && (
          <>
            <VStack spacing={3}>
              <InputMaskForm
                name="zipCode"
                onBlur={onBlurCep}
                type="text"
                mask="99999-999"
                placeholder="99999-999"
                control={control}
                label="CEP"
                rules={{
                  required: 'Campo obrigatório',
                  minLength: {
                    value: 9,
                    message: 'CEP inválido',
                  },
                }}
              />

              <InputForm
                type="text"
                name="street"
                placeholder="Insira o nome da rua"
                control={control}
                label="Rua"
                rules={{
                  required: 'Campo obrigatório',
                }}
              />

              <InputForm
                type="text"
                name="number"
                placeholder="Insira o número"
                control={control}
                label="Número"
                rules={{
                  required: 'Campo obrigatório',
                }}
              />

              <InputForm
                type="text"
                name="complement"
                placeholder="Insira o complemento"
                control={control}
                label="Complemento"
                rules={{
                  required: 'Campo obrigatório',
                }}
              />

              <InputForm
                type="text"
                name="district"
                placeholder="Insira o bairro"
                control={control}
                label="Bairro"
                rules={{
                  required: 'Campo obrigatório',
                }}
              />

              <InputForm
                type="text"
                name="city"
                placeholder="Insira a cidade"
                control={control}
                label="Cidade"
                isDisabled
                rules={{
                  required: 'Campo obrigatório',
                }}
              />

              <InputForm
                type="text"
                name="state"
                isDisabled
                placeholder="Insira o estado"
                control={control}
                label="Estado"
                rules={{
                  required: 'Campo obrigatório',
                }}
              />

              <HStack width="100%">
                <Button
                  width="100%"
                  mt={3}
                  colorScheme="brand"
                  onClick={() => goToPrevious()}
                  leftIcon={<FaArrowLeft />}
                >
                  Voltar
                </Button>
                <Button
                  width="100%"
                  mt={3}
                  type="submit"
                  colorScheme="brand"
                  rightIcon={<FaArrowRight />}
                >
                  Próximo
                </Button>
              </HStack>
            </VStack>
          </>
        )}

        {activeStep === 3 && (
          <VStack spacing={3}>
            <InputForm
              type="text"
              name="cardHolderName"
              placeholder="Insira o nome do titular"
              control={control}
              label="Nome do titular"
              rules={{
                required: 'Campo obrigatório',
                validate: {
                  valid: (value) => {
                    return value.split(' ').length > 1 || 'Nome inválido';
                  },
                },
              }}
            />
            <InputMaskForm
              type="text"
              name="cardHolderCpf"
              placeholder="999.999.999-99"
              control={control}
              mask="999.999.999-99"
              label="CPF do titular"
              rules={{
                required: 'Campo obrigatório',
                minLength: {
                  value: 11,
                  message: 'CPF inválido',
                },
                validate: {
                  valid: (value) => {
                    value = onlyNumbers(value);

                    return validCpf(value) || 'Documento inválido';
                  },
                },
              }}
            />

            <Divider />

            <InputMaskForm
              type="text"
              name="cardNumber"
              placeholder="XXXX XXXX XXXX XXXX"
              control={control}
              mask="9999 9999 9999 9999"
              label="Número do cartão"
              rules={{
                required: 'Campo obrigatório',
                validate: {
                  valid: (value) => {
                    value = onlyNumbers(value);
                    return testarCC(value) || 'Número do cartão inválido';
                  },
                },
              }}
            />
            <HStack width="100%">
              <InputMaskForm
                type="text"
                name="cardExpiration"
                mask="99/99"
                placeholder="MM/YY"
                control={control}
                label="Data de expiração"
                rules={{
                  required: 'Campo obrigatório',
                  minLength: {
                    value: 5,
                    message: 'Data inválida',
                  },
                  maxLength: {
                    value: 5,
                    message: 'Data inválida',
                  },
                  validate: {
                    valid: (value) => {
                      const [month, year] = value.split('/');
                      return parseInt(month) <= 12 && parseInt(month) > 0 && 2000 + parseInt(year) >= DateTime.now().year;
                    },
                  },
                }}
              />

              <InputMaskForm
                type="text"
                name="cardCvv"
                mask="9999"
                placeholder="CVV"
                control={control}
                label="CVV"
                rules={{
                  required: 'Campo obrigatório',
                  minLength: {
                    value: 3,
                    message: 'Código inválido',
                  },
                  maxLength: {
                    value: 4,
                    message: 'Código inválido',
                  },
                }}
              />
            </HStack>
            {method === 'CREDIT_CARD' && (
              <SelectForm
                name="installments"
                label="Parcelas"
                control={control}
                rules={{
                  required: 'Campo obrigatório',
                }}
                options={[
                  ...Array.from({ length: maxQuantiyParcelas }, (_, i) => {
                    const installment = i + 1;
                    const newAmount = calculateInstallment(amount, installment);
                    const finalMonthly = newAmount / installment;

                    return {
                      label: `${installment}x - ${maskCurrency(finalMonthly)} = ${maskCurrency(newAmount)}`,
                      value: installment.toString(),
                    };
                  }),
                ]}
              />
            )}
            <Divider />
            <Text
              fontSize="sm"
              textAlign="center"
            >
              Taxas de juros podem ser aplicadas na modalidade de pagamento de crédito.
            </Text>
            <Divider />
            <Text
              fontSize="sm"
              textAlign="center"
            >
              <>
                Ao clicar em pagar, você concorda com os{' '}
                <Link
                  href="/termos-de-uso"
                  isExternal
                  colorScheme="brand"
                >
                  termos de uso
                </Link>{' '}
                e{' '}
                <Link
                  href="/politica-de-privacidade"
                  isExternal
                >
                  política de privacidade
                </Link>
                .
              </>
            </Text>
            <HStack width="100%">
              <Button
                width="100%"
                mt={3}
                colorScheme="brand"
                onClick={() => goToPrevious()}
                leftIcon={<FaArrowLeft />}
              >
                Voltar
              </Button>
              <Button
                width="100%"
                mt={3}
                variant="solid"
                isLoading={isLoading}
                colorScheme="brand"
                type="submit"
                leftIcon={<MdLock size={22} />}
              >
                Realizar pagamento
              </Button>
            </HStack>
          </VStack>
        )}
      </form>
      <noscript>
        <iframe
          title="Equis anti-fraud session"
          style={{ width: '100px', height: '100px', border: '0', position: 'absolute', top: '-5000px' }}
          src={`https://h.online-metrix.net/fp/tags.js?org_id=k8vif92e&session_id=${antiFraudId}`}
        ></iframe>
      </noscript>
    </VStack>
  );
};

export default EquisCard;
